import { useCallback, useEffect, useRef, useState } from "react";
import { pedirCliente } from "./ir-al-cliente";

/**
 * Los planes de artículos del mes, desde Contenidos (23/10/2026).
 *
 * Jorge: «¿en contenidos se puede poner un panel donde crear el plan de los
 * artículos, y que se vea todo y se controle desde Contenidos?». Hasta hoy el
 * plan sólo se pedía por el chat.
 *
 * Arriba, cada cliente con web: cuántos artículos tiene ese mes y si hay un
 * plan esperando el sí. «Preparar plan» pide a BilboBot una propuesta con los
 * datos del cliente (lo que ya posiciona, semillas, Planner); Jorge la cambia
 * —keyword, título, fecha, desde dónde se enlaza— y pide el visto bueno. Es la
 * MISMA aprobación que la del chat: aprobada, se apunta en el calendario y en
 * Trello y se encarga la redacción.
 */

interface ClienteDelMes {
  clienteId: string;
  nombre: string;
  dominio: string | null;
  articulos: number;
  esperando: string | null;
}

interface Candidata {
  texto: string;
  intencion?: string;
  volumen?: number | null;
  posicion?: number | null;
  fuente: "proyecto" | "semilla" | "planner" | "posiciona";
  principal?: boolean;
  busquedasZona?: number;
  competencia?: string | null;
}

interface Articulo {
  keyword: string;
  intencion: "servicio" | "informativa";
  titulo: string;
  fecha: string;
  porQue: string;
  enlacesDesde: string[];
  volumen?: number | null;
}

interface Propuesta {
  cliente: { id: string; nombre: string; dominio: string | null };
  mes: string;
  cuantos: number;
  margenHoras: number;
  candidatas: Candidata[];
  paginas: string[];
  zona?: string;
  conGoogleAds?: boolean;
  articulos: Articulo[];
  problemas: string[];
  aviso: string | null;
}

async function json<T>(url: string, cuerpo?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: cuerpo === undefined ? "GET" : "POST",
    credentials: "same-origin",
    headers: cuerpo === undefined ? {} : { "content-type": "application/json" },
    ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
  });
  const d = (await r.json().catch(() => ({}))) as T & { error?: string; problemas?: string[] };
  if (!r.ok) {
    const e = new Error(d.error ?? `El servidor contestó ${r.status}`) as Error & { problemas?: string[] };
    if (d.problemas) e.problemas = d.problemas;
    throw e;
  }
  return d;
}

const mesVecino = (mes: string, d: number) => {
  const [a, m] = mes.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(a, m - 1 + d, 1)).toISOString().slice(0, 7);
};
const mesEnLetra = (mes: string) =>
  new Date(`${mes}-01T12:00:00Z`).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
const mesQueViene = () => mesVecino(new Date().toISOString().slice(0, 7), 1);
const corta = (u: string) => u.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") || u;

const COMPETENCIA: Record<string, string> = { LOW: "baja", MEDIUM: "media", HIGH: "alta" };

/** Lo que se busca en la zona según Google Ads, si se sabe: «880/mes · comp. media». */
const enGoogle = (c: Candidata) =>
  c.busquedasZona === undefined
    ? ""
    : ` · ${c.busquedasZona.toLocaleString("es-ES")}/mes${c.competencia && COMPETENCIA[c.competencia] ? ` · comp. ${COMPETENCIA[c.competencia]}` : ""}`;

const nombreDeLaFuente = (c: Candidata) =>
  (c.fuente === "proyecto"
    ? `${c.principal ? "★ " : ""}del proyecto${c.posicion ? ` · posición ${c.posicion}` : ""}`
    : c.fuente === "posiciona"
    ? `posición ${c.posicion}${c.volumen ? ` · ${c.volumen}/mes` : ""}`
    : c.fuente === "planner"
      ? `Planner${c.volumen ? ` · ${c.volumen}/mes` : ""}`
      : "semilla") + enGoogle(c);

export function ContenidosPlanes({ irA }: { irA?: (pantalla: string) => void } = {}) {
  const [mes, setMes] = useState(mesQueViene);
  const [clientes, setClientes] = useState<ClienteDelMes[] | null>(null);
  const [soloSinPlan, setSoloSinPlan] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);
  const [editando, setEditando] = useState<ClienteDelMes | null>(null);

  const cargar = useCallback(async () => {
    try {
      const d = await json<{ clientes: ClienteDelMes[] }>(`/api/contenidos/planes?mes=${mes}`);
      setClientes(d.clientes);
      setFallo(null);
    } catch (e) {
      setFallo((e as Error).message);
    }
  }, [mes]);
  useEffect(() => void cargar(), [cargar]);

  if (editando)
    return (
      <EditorDelPlan
        cliente={editando}
        mes={mes}
        {...(irA ? { irA } : {})}
        volver={() => {
          setEditando(null);
          void cargar();
        }}
      />
    );

  const lista = (clientes ?? []).filter((c) => !soloSinPlan || (c.articulos === 0 && !c.esperando));
  const conPlan = (clientes ?? []).filter((c) => c.articulos > 0).length;
  const esperando = (clientes ?? []).filter((c) => c.esperando).length;

  return (
    <section className="pnl-seccion cp-planes">
      <header className="cp-cabecera">
        <div>
          <h2>Planes de {mesEnLetra(mes)}</h2>
          {clientes && (
            <p className="pnl-sub">
              {clientes.length} clientes con web · <strong>{conPlan}</strong> con artículos ese mes
              {esperando > 0 && (
                <>
                  {" · "}
                  <span className="es-aviso">{esperando} esperan tu sí</span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="ct-mes" role="group" aria-label="Mes del plan">
          <button type="button" onClick={() => setMes(mesVecino(mes, -1))} aria-label="El mes anterior">
            ‹
          </button>
          <span>{mesEnLetra(mes)}</span>
          <button type="button" onClick={() => setMes(mesVecino(mes, 1))} aria-label="El mes siguiente">
            ›
          </button>
        </div>
      </header>

      <div className="pnl-filtros" role="group" aria-label="Qué clientes ver">
        <button className="pnl-chip" aria-pressed={soloSinPlan} onClick={() => setSoloSinPlan(true)}>
          Sin plan
        </button>
        <button className="pnl-chip" aria-pressed={!soloSinPlan} onClick={() => setSoloSinPlan(false)}>
          Todos
        </button>
      </div>

      {fallo && <div className="pnl-nota es-mal">{fallo}</div>}
      {!clientes ? (
        <p className="pnl-sub">Mirando…</p>
      ) : lista.length === 0 ? (
        <p className="pnl-vacio">
          {soloSinPlan ? "Todos los clientes con web tienen plan o lo tienen pedido ese mes." : "No hay clientes con web."}
        </p>
      ) : (
        <ul className="cp-clientes">
          {lista.map((c) => (
            <li key={c.clienteId} className="cp-cliente">
              <div className="cp-quien">
                <strong>{c.nombre}</strong>
                {c.dominio && <span className="pnl-sub">{c.dominio}</span>}
              </div>
              <span className={`cp-estado ${c.esperando ? "es-aviso" : c.articulos > 0 ? "es-ok" : "es-apagado"}`}>
                {c.esperando
                  ? "Plan esperando tu sí"
                  : c.articulos > 0
                    ? `${c.articulos} ${c.articulos === 1 ? "artículo" : "artículos"}`
                    : "Sin plan"}
              </span>
              {c.esperando ? (
                <a className="pnl-boton" href="#tareas">
                  Verlo en Tareas pendientes
                </a>
              ) : (
                <button type="button" className="pnl-boton si" onClick={() => setEditando(c)}>
                  {c.articulos > 0 ? "Añadir más" : "Preparar plan"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EditorDelPlan({
  cliente,
  mes,
  volver,
  irA,
}: {
  cliente: ClienteDelMes;
  mes: string;
  volver: () => void;
  irA?: (pantalla: string) => void;
}) {
  /** A las keywords del proyecto en su ficha SEO: allí se añaden, se quitan y se cambian. */
  const irALasKeywords = irA
    ? () => {
        pedirCliente(cliente.clienteId, "keywords", "contenidos");
        irA("memoria");
      }
    : null;
  const [p, setP] = useState<Propuesta | null>(null);
  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [problemas, setProblemas] = useState<string[]>([]);
  const [cuantos, setCuantos] = useState(2);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);
  const [pidiendo, setPidiendo] = useState(false);
  const [pedida, setPedida] = useState<string | null>(null);
  const [aprobada, setAprobada] = useState(false);
  /** Lo escrito en el buscador de páginas de cada artículo. */
  const [buscaPagina, setBuscaPagina] = useState<Record<number, string>>({});

  const proponer = useCallback(
    async (n?: number) => {
      setCargando(true);
      setFallo(null);
      try {
        const d = await json<Propuesta>("/api/contenidos/plan/proponer", {
          clienteId: cliente.clienteId,
          mes,
          ...(n ? { cuantos: n } : {}),
        });
        setP(d);
        setCuantos(d.cuantos);
        setArticulos(d.articulos);
        setProblemas(d.problemas);
      } catch (e) {
        setFallo((e as Error).message);
      } finally {
        setCargando(false);
      }
    },
    [cliente.clienteId, mes],
  );
  useEffect(() => void proponer(), [proponer]);

  /* Se revisa solo al cambiar algo, medio segundo después de dejar de escribir. */
  const reloj = useRef<number | null>(null);
  function cambiar(nuevos: Articulo[]) {
    setArticulos(nuevos);
    if (reloj.current) window.clearTimeout(reloj.current);
    reloj.current = window.setTimeout(() => {
      void json<{ problemas: string[] }>("/api/contenidos/plan/revisar", {
        clienteId: cliente.clienteId,
        mes,
        articulos: nuevos,
      })
        .then((r) => setProblemas(r.problemas))
        .catch(() => undefined);
    }, 500);
  }
  const uno = (i: number, cambio: Partial<Articulo>) => cambiar(articulos.map((a, j) => (j === i ? { ...a, ...cambio } : a)));

  async function pedir() {
    setPidiendo(true);
    setFallo(null);
    try {
      const r = await json<{ aprobacion: string }>("/api/contenidos/plan/pedir", {
        clienteId: cliente.clienteId,
        mes,
        articulos,
      });
      setPedida(r.aprobacion);
    } catch (e) {
      const err = e as Error & { problemas?: string[] };
      if (err.problemas) setProblemas(err.problemas);
      setFallo(err.message);
    } finally {
      setPidiendo(false);
    }
  }

  async function aprobarYa() {
    if (!pedida) return;
    setPidiendo(true);
    try {
      await json(`/api/hoy/aprobar/${pedida}`, { aprobada: true });
      setAprobada(true);
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setPidiendo(false);
    }
  }

  const primerDia = `${mes}-01`;
  const ultimoDia = new Date(Date.UTC(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0)).toISOString().slice(0, 10);

  return (
    <section className="pnl-seccion cp-editor">
      <header className="cp-cabecera">
        <div>
          <h2>
            Plan de {cliente.nombre} — {mesEnLetra(mes)}
          </h2>
          <p className="pnl-sub">
            {cliente.dominio} · nada se apunta ni se escribe hasta que lo apruebes
          </p>
        </div>
        <div className="cp-botones">
          {irALasKeywords && (
            <button type="button" className="pnl-boton" onClick={irALasKeywords}>
              Editar las keywords del proyecto
            </button>
          )}
          <button type="button" className="pnl-boton plano" onClick={volver}>
            ← Volver
          </button>
        </div>
      </header>

      {cargando ? (
        <p className="pnl-sub">BilboBot está mirando lo que posiciona {cliente.nombre} y preparando la propuesta…</p>
      ) : !p ? (
        <div className="pnl-nota es-mal">{fallo}</div>
      ) : pedida ? (
        <div className="cp-hecho">
          {aprobada ? (
            <p>
              <strong>Aprobado.</strong> Se apuntan los {articulos.length} artículos en el calendario y en su ficha de
              Trello, y se encarga la redacción. Los verás avanzar en «Calendario».
            </p>
          ) : (
            <p>
              <strong>Pedido el visto bueno.</strong> Lo tienes en Tareas pendientes, o apruébalo ya aquí.
            </p>
          )}
          <div className="cp-botones">
            {!aprobada && (
              <button type="button" className="pnl-boton si" disabled={pidiendo} onClick={() => void aprobarYa()}>
                Aprobar ya
              </button>
            )}
            <button type="button" className="pnl-boton" onClick={volver}>
              Volver a los planes
            </button>
          </div>
          {fallo && <div className="pnl-nota es-mal">{fallo}</div>}
        </div>
      ) : (
        <>
          {p.aviso && <div className="pnl-nota">{p.aviso}</div>}
          <details className="cp-keywords">
            <summary>
              Las {p.candidatas.length} keywords del proyecto, de donde salen los artículos
              {p.conGoogleAds && p.zona ? ` · búsquedas reales en ${p.zona} (Google Ads)` : ""}
            </summary>
            <p className="pnl-sub">
              Primero las del proyecto (las que has elegido atacar, ★ las principales), luego las que ya posicionan
              entre la 4 y la 20, las del Planner con su volumen y las semillas. Un artículo sólo puede ir a una de
              éstas.
              {irALasKeywords && (
                <>
                  {" "}
                  <button type="button" className="pnl-boton plano" onClick={irALasKeywords}>
                    Editarlas →
                  </button>
                </>
              )}
            </p>
            <ul>
              {p.candidatas.map((c) => (
                <li key={c.texto}>
                  <span>{c.texto}</span>
                  <span className={`cp-fuente cp-${c.fuente}`}>{nombreDeLaFuente(c)}</span>
                  {articulos.some((a) => a.keyword === c.texto) && <span className="cp-usada">en el plan</span>}
                </li>
              ))}
            </ul>
          </details>
          <div className="cp-barra">
            <label>
              Artículos este mes{" "}
              <input
                type="number"
                min={1}
                max={8}
                value={cuantos}
                onChange={(e) => setCuantos(Math.max(1, Math.min(8, Number(e.target.value) || 1)))}
              />
            </label>
            <button type="button" className="pnl-boton" onClick={() => void proponer(cuantos)}>
              Volver a proponer
            </button>
            <span className="pnl-sub">
              {p.candidatas.length} keywords con dato · {p.paginas.length} páginas conocidas
            </span>
          </div>

          <ol className="cp-articulos">
            {articulos.map((a, i) => (
              <li key={i} className="cp-articulo">
                <div className="cp-fila">
                  <label className="cp-campo cp-ancho">
                    <span>Keyword</span>
                    <select value={a.keyword} onChange={(e) => uno(i, { keyword: e.target.value })}>
                      {!p.candidatas.some((c) => c.texto === a.keyword) && <option value={a.keyword}>{a.keyword || "—"}</option>}
                      {p.candidatas.map((c) => (
                        <option key={c.texto} value={c.texto}>
                          {c.texto} ({nombreDeLaFuente(c)})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="cp-campo">
                    <span>Tipo</span>
                    <select
                      value={a.intencion}
                      onChange={(e) => uno(i, { intencion: e.target.value as Articulo["intencion"] })}
                    >
                      <option value="servicio">De servicio</option>
                      <option value="informativa">Informativo</option>
                    </select>
                  </label>
                  <label className="cp-campo">
                    <span>Sale el</span>
                    <input
                      type="date"
                      min={primerDia}
                      max={ultimoDia}
                      value={a.fecha}
                      onChange={(e) => uno(i, { fecha: e.target.value })}
                    />
                  </label>
                </div>
                <label className="cp-campo">
                  <span>Título</span>
                  <input type="text" value={a.titulo} onChange={(e) => uno(i, { titulo: e.target.value })} />
                </label>
                <label className="cp-campo">
                  <span>Por qué</span>
                  <input type="text" value={a.porQue} onChange={(e) => uno(i, { porQue: e.target.value })} />
                </label>
                <div className="cp-campo">
                  <span>Se enlaza desde</span>
                  <input
                    type="search"
                    className="cp-buscar"
                    placeholder={`Buscar entre las ${p.paginas.length} páginas de la web…`}
                    value={buscaPagina[i] ?? ""}
                    onChange={(e) => setBuscaPagina({ ...buscaPagina, [i]: e.target.value })}
                  />
                  <div className="cp-paginas">
                    {[
                      ...new Set([
                        ...a.enlacesDesde,
                        ...p.paginas
                          .filter((u) => !(buscaPagina[i] ?? "").trim() || u.toLowerCase().includes((buscaPagina[i] ?? "").trim().toLowerCase()))
                          .slice(0, 12),
                      ]),
                    ].map((u) => {
                      const si = a.enlacesDesde.includes(u);
                      return (
                        <button
                          type="button"
                          key={u}
                          className="pnl-chip"
                          aria-pressed={si}
                          title={u}
                          onClick={() =>
                            uno(i, { enlacesDesde: si ? a.enlacesDesde.filter((x) => x !== u) : [...a.enlacesDesde, u] })
                          }
                        >
                          {corta(u)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  className="pnl-boton no cp-quitar"
                  onClick={() => cambiar(articulos.filter((_, j) => j !== i))}
                >
                  Quitar este artículo
                </button>
              </li>
            ))}
          </ol>
          <button
            type="button"
            className="pnl-boton plano"
            onClick={() =>
              cambiar([
                ...articulos,
                {
                  keyword: p.candidatas[0]?.texto ?? "",
                  intencion: "servicio",
                  titulo: "",
                  fecha: "",
                  porQue: "",
                  enlacesDesde: [],
                },
              ])
            }
          >
            + Añadir un artículo a mano
          </button>

          {problemas.length > 0 && (
            <div className="pnl-nota es-aviso cp-problemas">
              <p>
                <strong>Antes de pedir el sí, arregla esto:</strong>
              </p>
              <ul>
                {problemas.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          )}
          {fallo && problemas.length === 0 && <div className="pnl-nota es-mal">{fallo}</div>}
          <div className="cp-botones">
            <button
              type="button"
              className="pnl-boton si"
              disabled={pidiendo || problemas.length > 0 || articulos.length === 0}
              onClick={() => void pedir()}
            >
              {pidiendo ? "Pidiendo…" : `Pedir el visto bueno · ${articulos.length} artículo${articulos.length === 1 ? "" : "s"}`}
            </button>
            <span className="pnl-sub">
              Aprobado, se apunta en el calendario y en Trello y se encarga la redacción. Se publica en su web con{" "}
              {p.margenHoras} h de margen mínimo.
            </span>
          </div>
        </>
      )}
    </section>
  );
}
